import React, { useEffect, useRef, useState } from "react";
import { drop, floor, map, size, take } from "lodash";
import { animated, useSpring } from "react-spring";
import styled from "styled-components";
import {
  FontSize,
  FontWeight,
  Radius,
  Spacing,
  content,
  cover,
  form,
  transparentize,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { useSize } from "@lkf/hooks";
import { tr } from "@radarly/i18n";
import { DatavizName } from "@radarly/models/dataviz";
import { PastedCard } from "@radarly/models/insight-pages/editor";

type Section = {
  id: string;
  title: string;
  sections?: Omit<Section, "sections">[];
  cards?: Card[];
};

type Card = {
  viz: DatavizName;
  id: string;
};

export interface IProps {
  sections: Section[];
  cards: Card[];
  renderViz: (viz: DatavizName, i: string) => React.ReactNode;
  renderPastedViz: (pastedCard: PastedCard) => React.ReactNode;
  selectedViz?: string;
  anchor: string;
  locale: Locale;
  pastedCards: PastedCard[];
}

const ITEM_WIDTH = 182;
const GAP = 24;

const Viz = styled.div`
  display: flex;
  column-gap: ${Spacing.Large};
  row-gap: ${Spacing.Large};
  flex-wrap: wrap;
  > * {
    flex: 0 0 auto;
  }
`;

const Title = styled.div``;

const MainSection = styled.div`
  & + & {
    margin-top: 36px;
  }
  ${Title} {
    font-size: ${FontSize.Large};
    font-weight: ${FontWeight.Bold};
    margin-bottom: 6px;
    color: ${content};
  }
  ${Viz} {
    margin-top: 12px;
  }
`;

const SubSection = styled(MainSection)`
  & + & {
    margin-top: 24px;
  }
  ${Title} {
    color: ${(props) => content(props, "secondary")};
    font-size: ${FontSize.Medium};
    font-weight: ${FontWeight.Normal};
  }
  ${Viz} {
    margin-top: 6px;
  }
`;

const ShowMore = styled.div`
  position: relative;
  padding: ${Spacing.Medium};
  width: ${ITEM_WIDTH}px;
  height: 122px;
  border-radius: ${Radius.Medium};
  border: 1px dashed ${form};
  text-align: center;
  font-weight: ${FontWeight.Normal};
  cursor: pointer;
  :hover {
    border-color: transparent;
    &:after {
      content: "";
      ${cover(-1)};
      border-radius: inherit;
      background-color: ${(props) =>
        transparentize(0.8, form(props, "primary"))};
    }
  }
`;

const Plus = styled.div`
  line-height: 48px;
  color: ${(props) => content(props, "secondary")};
  font-size: ${FontSize.ExtraLarge};
`;

const Message = styled.div`
  margin-top: ${Spacing.Medium};
  line-height: 38px;
  color: ${form};
`;

const PastedCardList = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const VizSection = ({
  cards,
  renderViz,
  initiallyExpanded,
  locale,
}: {
  cards: {
    viz: DatavizName;
    id: string;
  }[];
  renderViz: IProps["renderViz"];
  initiallyExpanded?: boolean;
  locale: Locale;
}) => {
  const ref = useRef(null);
  const { width } = useSize(ref);
  const itemCount = size(cards);
  const maxItemsInRow = floor((width + GAP) / (ITEM_WIDTH + GAP));
  const itemsFitOnSingleRow = itemCount <= maxItemsInRow; // Does all the items fit on a single row?
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const visibleItems = itemsFitOnSingleRow
    ? cards
    : take(cards, maxItemsInRow - 1);
  const hiddenItems = itemsFitOnSingleRow ? [] : drop(cards, maxItemsInRow - 1);

  const props = useSpring({
    height: expanded ? 122 : 0,
    opacity: expanded ? 1 : 0,
    transform: `scale(${expanded ? 1 : 0.6})`,
  });

  return (
    <>
      {!!size(ref) && (
        <Viz ref={ref}>
          {map(visibleItems, ({ viz, id }) => renderViz(viz, id))}
          {!itemsFitOnSingleRow && !expanded && (
            <ShowMore onClick={() => setExpanded(true)}>
              <Plus>+{itemCount - maxItemsInRow + 1}</Plus>
              <Message>{tr(locale, "CUSTOM_PAGES::EDITOR::SHOW_ALL")}</Message>
            </ShowMore>
          )}
          {expanded &&
            map(hiddenItems, ({ viz, id }, idx) => (
              <animated.div style={props} key={idx}>
                {renderViz(viz, id)}
              </animated.div>
            ))}
        </Viz>
      )}
    </>
  );
};

const PanelsCardLibraryCards = ({
  sections,
  cards,
  pastedCards,
  renderViz,
  renderPastedViz,
  anchor,
  locale,
}: IProps) => {
  const structure = size(sections)
    ? sections
    : [{ id: null, title: null, cards }];

  const refs = structure.reduce((acc, section) => {
    acc[section.id] = React.createRef();
    if (!section.sections && section.cards) {
      section.cards.reduce((acc, section) => {
        acc[section.id] = React.createRef();
        return acc;
      }, acc);
    }
    (section.sections || []).reduce((acc, section) => {
      acc[section.id] = React.createRef();
      return acc;
    }, acc);
    return acc;
  }, {});

  useEffect(() => {
    if (anchor !== null && refs[anchor]) {
      refs[anchor].current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [anchor, refs]);

  return (
    <>
      {pastedCards.length > 0 && (
        <MainSection>
          {
            <Title>
              {tr(locale, "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::PASTED")}
            </Title>
          }
          <PastedCardList>
            {map(pastedCards, (pastedCard, i) => (
              <div
                key={`pasted_${i}`}
                style={{
                  marginTop: Spacing.ExtraSmall,
                  marginRight: i === pastedCards.length - 1 ? 0 : Spacing.Large,
                }}
              >
                {renderPastedViz(pastedCard)}
              </div>
            ))}
          </PastedCardList>
        </MainSection>
      )}
      {map(structure, ({ id, title, sections, cards }) => (
        <MainSection key={id} id={id} ref={refs[id]}>
          {title && <Title>{title}</Title>}
          <VizSection
            {...{ cards, renderViz, locale }}
            initiallyExpanded={!size(sections)}
          />
          {sections &&
            map(sections, ({ id, title, cards }) => (
              <SubSection key={id} ref={refs[id]}>
                {title && <Title>{title}</Title>}
                <VizSection {...{ cards, renderViz, locale }} />
              </SubSection>
            ))}
        </MainSection>
      ))}
    </>
  );
};

export default PanelsCardLibraryCards;
